/**
 * Centralized error tracking utility
 * 
 * Generates application-specific error IDs that are easily distinguishable
 * from Lovable platform error IDs (32-char hex without dashes).
 * 
 * Error ID Prefixes:
 * - APP-*  : Frontend React component errors
 * - EF-*   : Edge Function errors
 * - 32-char hex : Lovable Platform (report to support)
 */

export interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  locale?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Generate an application-specific error ID
 * Format: APP-{timestamp_base36}-{random_6chars}
 * Example: APP-lq2x4c9-abc123
 */
export function generateErrorId(): string {
  const timestamp = Date.now().toString(36);
  const random = Math.random().toString(36).slice(2, 8);
  return `APP-${timestamp}-${random}`;
}

/**
 * Track an error with context and return a traceable ID
 */
export function trackError(error: Error, context: ErrorContext = {}): string {
  const errorId = generateErrorId();
  
  const errorLog = {
    errorId,
    message: error.message,
    name: error.name,
    stack: error.stack,
    ...context,
    timestamp: new Date().toISOString(),
  };

  // Log structured error for debugging
  // In production, this could integrate with Sentry/LogRocket
  if (import.meta.env.DEV) {
    console.error('[ERROR_TRACKING]', errorLog);
  } else {
    // Production: minimal log to avoid exposing internals
    console.error('[ERROR]', errorId, error.message);
  }

  return errorId;
}

/**
 * Check if an error ID is from the Lovable platform
 * Platform IDs are 32-character hex strings without dashes
 */
export function isPlatformErrorId(errorId: string): boolean {
  return /^[a-f0-9]{32}$/i.test(errorId);
}

/**
 * Get guidance message based on error ID format
 */
export function getErrorGuidance(errorId: string): string {
  if (errorId.startsWith('APP-')) {
    return 'This is an application error. Please report with the error ID and steps to reproduce.';
  }
  if (errorId.startsWith('EF-')) {
    return 'This is a server error. Check the edge function logs for details.';
  }
  if (isPlatformErrorId(errorId)) {
    return 'This is a platform error. Please contact Lovable support with this ID and timestamp.';
  }
  return 'Unknown error type. Please report with full details.';
}
